<template>
    <div class="video-player" ref="videoContainer">
        <vue-plyr ref="player" :options="playerOptions">
            <div v-if="isVimeo" ref="vimeoPlayer"></div>
            <div v-else-if="isYouTube" ref="youtubePlayer"></div>
        </vue-plyr>

        <div class="controls">
            <button @click="togglePlay">{{ isPlaying ? "Pause" : "Play" }}</button>
            <button @click="toggleFullScreen">Fullscreen</button>
            <button @click="togglePictureInPicture">PiP</button>

            <label
                >Quality:
                <select v-model="selectedQuality" @change="changeQuality">
                    <option
                        v-for="quality in availableQualities"
                        :key="quality"
                        :value="quality"
                    >
                        {{ quality }}
                    </option>
                </select>
            </label>

            <label
                >Speed:
                <select v-model="selectedSpeed" @change="changeSpeed">
                    <option v-for="speed in speedList" :key="speed" :value="speed">
                        {{ speed }}x
                    </option>
                </select>
            </label>
        </div>
    </div>
</template>

<script>
import VuePlyr from "vue-plyr";
import "plyr/dist/plyr.css";
import Player from "@vimeo/player"; // Vimeo API
import { resolveVimeoVideo } from "@/utils/vimeo";

export default {
    components: { VuePlyr },
    props: {
        videoSrc: String,
        platform: String, // "vimeo" или "youtube"
    },
    data() {
        return {
            player: null,
            vimeoPlayer: null,
            youtubePlayer: null,
            isPlaying: false,
            selectedQuality: "auto",
            availableQualities: ["auto"], // Заполняется позже для YouTube
            selectedSpeed: 1,
            speedList: [0.25, 0.5, 1, 1.25, 1.5, 2],
            playerOptions: {
                settings: ["quality", "speed"],
                controls: [],
            },
        };
    },
    computed: {
        isVimeo() {
            return this.platform === "vimeo";
        },
        isYouTube() {
            return this.platform === "youtube";
        },
        videoId() {
            const match =
                this.videoSrc.match(
                    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/.*[?&]v=([^&]+)/
                ) || this.videoSrc.match(/(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?&]+)/);
            return match ? match[1] : this.videoSrc;
        },
    },
    mounted() {
        if (this.isVimeo) {
            this.initVimeoPlayer();
        } else if (this.isYouTube) {
            this.initYouTubePlayer();
        }
    },
    methods: {
        async initVimeoPlayer() {
            // Supports old numeric links (vimeo.com/123, with optional privacy
            // hash) and the new share links (vimeo.com/share/<UUID>).
            const resolved = await resolveVimeoVideo(this.videoSrc);

            if (!resolved) {
                console.error("Unrecognized Vimeo link:", this.videoSrc);
                return;
            }

            const options = { responsive: true, autoplay: false };

            if (resolved.hash) {
                options.url = `https://vimeo.com/${resolved.id}/${resolved.hash}`;
            } else {
                options.id = resolved.id;
            }

            this.vimeoPlayer = new Player(this.$refs.vimeoPlayer, options);

            this.vimeoPlayer.on("play", () => (this.isPlaying = true));
            this.vimeoPlayer.on("pause", () => (this.isPlaying = false));

            this.vimeoPlayer
                .getQualities()
                .then((qualities) => {
                    this.availableQualities = qualities.map((q) => q.id);
                })
                .catch((error) =>
                    console.error("Ошибка получения качеств Vimeo:", error)
                );
        },
        initYouTubePlayer() {
            if (!window.YT) {
                const tag = document.createElement("script");
                tag.src = "https://www.youtube.com/iframe_api";
                const firstScriptTag = document.getElementsByTagName("script")[0];
                firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

                window.onYouTubeIframeAPIReady = this.createYouTubePlayer;
            } else {
                this.createYouTubePlayer();
            }
        },
        createYouTubePlayer() {
            if (this.$refs.youtubePlayer) {
                this.youtubePlayer = new window.YT.Player(this.$refs.youtubePlayer, {
                    videoId: this.videoId,
                    playerVars: {
                        playsinline: 1,
                        enablejsapi: 1,
                        rel: 0,
                    },
                    events: {
                        onStateChange: this.onYouTubeStateChange,
                        onReady: this.onYouTubeReady,
                    },
                });
            }
        },
        onYouTubeStateChange(event) {
            switch (event.data) {
                case window.YT.PlayerState.PLAYING:
                    this.isPlaying = true;
                    break;
                case window.YT.PlayerState.PAUSED:
                case window.YT.PlayerState.ENDED:
                    this.isPlaying = false;
                    break;
            }
        },
        onYouTubeReady(event) {
            this.youtubePlayer = event.target;

            // Задержка для получения доступных уровней качества
            setTimeout(() => {
                const availableLevels = this.youtubePlayer.getAvailableQualityLevels();

                if (availableLevels.length) {
                    const allPossibleQualities = [
                        "highres",
                        "hd2160",
                        "hd1440",
                        "hd1080",
                        "hd720",
                        "large",
                        "medium",
                        "small",
                        "tiny",
                        "auto",
                    ];

                    // Сортируем и фильтруем по доступным уровням
                    this.availableQualities = allPossibleQualities.filter((quality) =>
                        availableLevels.includes(quality)
                    );

                    // Если "auto" не найден, добавляем его
                    if (!this.availableQualities.includes("auto")) {
                        this.availableQualities.push("auto");
                    }

                    this.selectedQuality = "auto"; // Устанавливаем "auto" по умолчанию
                } else {
                    console.warn("Доступные уровни качества не найдены");
                    this.availableQualities = ["auto"];
                }
            }, 1000); // Задержка в 1 секунду для более точных данных
        },
        togglePlay() {
            if (this.isVimeo) {
                this.vimeoPlayer.getPaused().then((paused) => {
                    if (paused) {
                        this.vimeoPlayer.play();
                    } else {
                        this.vimeoPlayer.pause();
                    }
                });
            } else if (this.isYouTube && this.youtubePlayer) {
                const playerState = this.youtubePlayer.getPlayerState();
                if (playerState === window.YT.PlayerState.PLAYING) {
                    this.youtubePlayer.pauseVideo();
                } else {
                    this.youtubePlayer.playVideo();
                }
            }
        },
        toggleFullScreen() {
            const elem = this.$refs.videoContainer;
            if (!document.fullscreenElement) {
                elem.requestFullscreen();
            } else {
                document.exitFullscreen();
            }
        },
        togglePictureInPicture() {
            if (this.isVimeo) {
                this.vimeoPlayer.requestPictureInPicture().catch((error) => {
                    console.error("Ошибка PiP Vimeo:", error);
                });
            } else if (this.isYouTube && document.pictureInPictureEnabled) {
                this.$refs.youtubePlayer.requestPictureInPicture();
            }
        },
        changeQuality() {
            if (this.isYouTube) {
                this.youtubePlayer.setPlaybackQuality(this.selectedQuality);
            } else if (this.isVimeo) {
                this.vimeoPlayer.setQuality(this.selectedQuality).catch((error) => {
                    console.error("Ошибка смены качества Vimeo:", error);
                });
            }
        },
        changeSpeed() {
            if (this.isVimeo) {
                this.vimeoPlayer.setPlaybackRate(this.selectedSpeed);
            } else if (this.isYouTube) {
                this.youtubePlayer.setPlaybackRate(this.selectedSpeed);
            }
        },
    },
};
</script>

<style scoped>
.video-player {
    position: relative;
    max-width: 800px;
    margin: auto;
}

.controls {
    display: flex;
    justify-content: space-around;
    margin-top: 10px;
    background: rgba(0, 0, 0, 0.7);
    padding: 10px;
    border-radius: 5px;
    color: white;
}

button {
    color: white;
}
</style>
